import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Search, Truck, MapPin, ArrowRight, CalendarClock, FileText,
  CheckCircle2, Loader2, Mail, Radio,
} from "lucide-react";
import { Reveal, PageHero } from "@/components/bits";
import { COMPANY } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = ["Tendered", "Picked up", "In Transit", "At Delivery", "Delivered"];
const STATUS_INDEX = { tendered: 0, picked_up: 1, in_transit: 2, at_delivery: 3, delivered: 4 };

const DEMOS = [
  { id: "FF-248903", label: "In transit" },
  { id: "FF-517326", label: "Reefer pickup" },
  { id: "FF-862091", label: "Delivered" },
];

function Stepper({ status }) {
  const current = STATUS_INDEX[status] ?? 0;
  return (
    <div className="flex items-start" data-testid="track-stepper">
      {STEPS.map((s, i) => (
        <div key={s} className="flex-1 last:flex-none">
          <div className="flex items-center">
            <span
              className={`h-3.5 w-3.5 rounded-full shrink-0 border-2 transition-colors ${
                i < current || (i === current && status === "delivered")
                  ? "bg-corpblue border-corpblue"
                  : i === current
                  ? "bg-white border-signal live-dot"
                  : "bg-slate-300 border-slate-300"
              }`}
            />
            {i < STEPS.length - 1 && (
              <span className={`h-0.5 flex-1 mx-1 ${i < current ? "bg-corpblue" : "bg-slate-300"}`} />
            )}
          </div>
          <p className={`mt-2 font-label text-[9px] sm:text-[10px] uppercase tracking-[0.12em] font-semibold ${i <= current ? "text-ink" : "text-slate-400"}`}>
            {s}
          </p>
        </div>
      ))}
    </div>
  );
}

function ShipmentResult({ s }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className="card"
      data-testid="track-result-card"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 px-6 sm:px-10 py-5 border-b border-slate-100 bg-paper/70">
        <div className="flex items-center gap-3">
          <Truck className="h-5 w-5 text-corpblue" />
          <p className="font-display text-sm font-bold tracking-[0.1em] text-ink" data-testid="track-result-id">{s.tracking_id}</p>
        </div>
        <span className="bg-signal text-white font-display font-bold text-sm rounded-full px-4 py-1.5" data-testid="track-status-badge">
          {s.status_label}
        </span>
      </div>

      <div className="px-6 sm:px-10 py-8">
        <div className="flex flex-wrap items-center gap-4 sm:gap-6" data-testid="track-result-route">
          <div>
            <p className="font-label text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold">Origin</p>
            <p className="font-display text-lg sm:text-2xl font-bold text-ink">{s.origin}</p>
          </div>
          <ArrowRight className="h-5 w-5 text-signal shrink-0" />
          <div>
            <p className="font-label text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold">Destination</p>
            <p className="font-display text-lg sm:text-2xl font-bold text-ink">{s.destination}</p>
          </div>
        </div>

        <div className="mt-7">
          <Stepper status={s.status} />
        </div>

        <dl className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-5">
          {[
            ["Carrier", s.carrier, Truck],
            ["Equipment", s.equipment, FileText],
            ["ETA", s.eta, CalendarClock],
          ].map(([k, v, Icon]) => (
            <div key={k} className="rounded-2xl bg-paper px-5 py-4">
              <dt className="flex items-center gap-2 font-label text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-500">
                <Icon className="h-3.5 w-3.5 text-corpblue" /> {k}
              </dt>
              <dd className="mt-1.5 text-[15px] font-semibold text-ink" data-testid={`track-result-${k.toLowerCase()}`}>{v}</dd>
            </div>
          ))}
          {s.reference_po && (
            <div className="rounded-2xl bg-paper px-5 py-4">
              <dt className="flex items-center gap-2 font-label text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-500">
                <FileText className="h-3.5 w-3.5 text-corpblue" /> Reference PO
              </dt>
              <dd className="mt-1.5 text-[15px] font-semibold text-ink" data-testid="track-result-po">{s.reference_po}</dd>
            </div>
          )}
        </dl>

        <div className="mt-9 border-t border-slate-100 pt-7">
          <p className="font-label text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-5">Shipment history</p>
          <ol className="relative border-l-2 border-slate-200 ml-2 space-y-6" data-testid="track-timeline">
            {s.events.map((ev, i) => (
              <li key={i} className="ml-6" data-testid={`track-event-${i}`}>
                <span className={`absolute -left-[9px] mt-1.5 h-4 w-4 rounded-full border-2 border-white ${ev.done ? "bg-corpblue" : "bg-slate-300"}`} />
                <p className="font-label text-[11px] uppercase tracking-[0.12em] font-semibold text-corpblue">{ev.time}</p>
                <p className="mt-1 font-display font-bold text-ink">{ev.description}</p>
                <p className="mt-0.5 text-sm text-slate-500 flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5" /> {ev.location}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </motion.div>
  );
}

function QuoteResult({ q }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className="card border border-corpblue/20"
      data-testid="track-quote-card"
    >
      <div className="px-6 sm:px-10 py-8">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="h-6 w-6 text-corpblue" />
          <p className="font-display text-xl font-bold text-ink">Quote request {q.ref} received</p>
        </div>
        <p className="mt-3 text-slate-600 leading-relaxed max-w-xl">
          Our dispatch desk is preparing your rate for <strong className="text-ink">{q.origin} → {q.destination}</strong> ({q.freight_type}).
          It will arrive at the email you provided shortly — quotes usually come back within the hour during dispatch hours.
        </p>
        <p className="mt-4 font-label text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">
          Submitted {q.created_at ? q.created_at.slice(0, 10) : "recently"} (UTC)
        </p>
      </div>
    </motion.div>
  );
}

export default function TrackPage() {
  const [tid, setTid] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const lookup = async (value) => {
    const v = (value ?? tid).trim();
    if (!v) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const { data } = await axios.get(`${API}/track/${encodeURIComponent(v)}`);
      setResult(data);
    } catch (e) {
      setError(
        e?.response?.status === 404
          ? e.response.data.detail
          : "Tracking is unavailable right now — please try again shortly."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <main>
      <PageHero
        eyebrow="Tracking Portal"
        title="Where's my freight?"
        testid="track-page-title"
        lead="Enter the reference from your quote confirmation or load tender — you'll get the live status, ETA and full history of your shipment."
      >
        <Reveal delay={0.24}>
          <div className="mt-8 card p-2 flex flex-col sm:flex-row gap-2 max-w-2xl shadow-lift">
            <input
              data-testid="track-input"
              aria-label="Shipment tracking reference"
              value={tid}
              onChange={(e) => setTid(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && lookup()}
              placeholder="Enter tracking reference, e.g. FF-248903"
              className="min-w-0 flex-1 rounded-2xl bg-transparent px-5 py-3.5 font-display text-base uppercase text-ink placeholder:normal-case placeholder:font-sans placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-brand"
            />
            <button
              onClick={() => lookup()}
              disabled={loading}
              data-testid="track-submit-button"
              className="btn-pill bg-signal hover:bg-signalhover disabled:opacity-60 text-white px-8 py-3.5 hover:shadow-glow"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              {loading ? "Searching…" : "Track load"}
            </button>
          </div>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="track-demo-chips">
            <span className="label text-[10px] text-white mr-1">Try a demo:</span>
            {DEMOS.map((d) => (
              <button
                key={d.id}
                data-testid={`track-demo-${d.id.toLowerCase()}`}
                onClick={() => { setTid(d.id); lookup(d.id); }}
                className="rounded-full border border-white/50 bg-white/5 px-3.5 py-2.5 text-xs font-semibold text-white hover:bg-white/15 transition-colors"
              >
                {d.id} · {d.label}
              </button>
            ))}
          </div>
        </Reveal>
      </PageHero>

      <section className="py-14 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 min-h-[200px]">
          <AnimatePresence mode="wait">
            {loading && (
              <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex items-center gap-3 text-slate-500" role="status" data-testid="track-loading">
                <Loader2 className="h-5 w-5 animate-spin text-corpblue" /> Pulling up your load…
              </motion.div>
            )}
            {!loading && error && (
              <motion.div
                key="error"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="card p-8"
                data-testid="track-not-found"
              >
                <p className="font-display text-xl font-bold text-ink">Nothing found</p>
                <p className="mt-2 text-slate-600 leading-relaxed">{error}</p>
                <a
                  href={`mailto:${COMPANY.email}`}
                  data-testid="track-not-found-email"
                  className="mt-5 inline-flex items-center gap-2 font-display font-bold text-corpblue hover:text-signal transition-colors"
                >
                  <Mail className="h-4 w-4" /> Ask our dispatch desk
                </a>
              </motion.div>
            )}
            {!loading && result?.type === "shipment" && <ShipmentResult s={result.shipment} />}
            {!loading && result?.type === "quote" && <QuoteResult q={result.quote} />}
          </AnimatePresence>

          {!loading && !result && !error && (
            <Reveal delay={0.1}>
              <div className="card p-8 flex items-start gap-4" data-testid="track-empty-state">
                <Radio className="h-6 w-6 text-corpblue shrink-0" />
                <p className="text-slate-600 leading-relaxed">
                  <span className="font-display font-bold text-ink">Live updates, every leg.</span>{" "}
                  Every Ferry Freight load ships with a tracking reference — tender, pickup, transit
                  checkpoints and delivery, all in one view.
                </p>
              </div>
            </Reveal>
          )}
        </div>
      </section>
    </main>
  );
}
